import { NgIf } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { distinctUntilChanged } from 'rxjs';
import { SelectOption } from 'src/app/core/models/dictionary';
import { ToolsService } from 'src/app/core/services/tools.service';
import { UserService } from 'src/app/core/services/user.service';
import { CustomErrorMessage } from 'src/app/shared/models/custom-error-messages.model';
import { PaButtonComponent } from 'src/app/shared/ui-kit/pa-button/pa-button.component';
import { PaInputComponent } from 'src/app/shared/ui-kit/pa-input/pa-input.component';
import { PaMessageComponent } from 'src/app/shared/ui-kit/pa-message/pa-message.component';
import { PaSelectButtonComponent } from 'src/app/shared/ui-kit/pa-select-button/pa-select-button.component';
import { environment } from 'src/environments/environment';
import { SupportLinkV2Component } from '../../components/support-link/support-link.component';
import { AuthFlowId } from '../../models/auth-flow.models';
import { AuthFlowAnalyticsHelper } from '../../helpers/auth-analytics.helper';
import { AuthActionsService } from '../../services/auth-actions.service';
import { AuthFlowService } from '../../services/auth-flow.service';
import { AuthSessionStore } from '../../services/auth-session.store';
import { IosKeyboardHoldService } from '../../services/ios-keyboard-hold.service';

type AuthMode = 'login' | 'register';

@Component({
  selector: 'app-enter-email-step',
  standalone: true,
  imports: [
    NgIf,
    ReactiveFormsModule,
    CheckboxModule,
    PaButtonComponent,
    PaInputComponent,
    PaMessageComponent,
    PaSelectButtonComponent,
    SupportLinkV2Component,
  ],
  templateUrl: './enter-email-step.component.html',
  styleUrls: ['../../styles/_step.styles.scss'],
})
export class EnterEmailStepComponent implements OnInit {
  readonly environment = environment;

  readonly authModeOptions: SelectOption<AuthMode>[] = [
    { id: 'login', name: 'Вход' },
    { id: 'register', name: 'Регистрация' },
  ];

  readonly emailErrorMessages: CustomErrorMessage = {
    invalidEmail: 'Введите корректную почту.',
  };

  readonly form = new FormGroup({
    authMode: new FormControl<AuthMode>('login', { nonNullable: true }),
    email: new FormControl('', { nonNullable: true }),
    agreeOffer: new FormControl(false, { nonNullable: true }),
  });

  readonly authErrorMessage = signal<string | null>(null);

  private readonly store = inject(AuthSessionStore);
  private readonly flow = inject(AuthFlowService);
  private readonly actions = inject(AuthActionsService);
  private readonly authAnalytics = inject(AuthFlowAnalyticsHelper);
  private readonly toolsService = inject(ToolsService);
  private readonly user = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);
  readonly keyboardHold = inject(IosKeyboardHoldService);

  get authMode(): AuthMode {
    return this.form.controls.authMode.value;
  }

  ngOnInit(): void {
    const initialMode: AuthMode =
      this.flow.flowId() === AuthFlowId.Registration ? 'register' : 'login';
    const login = this.store.account().login || this.user.data.email || '';

    this.form.patchValue({ authMode: initialMode, email: login }, { emitEvent: false });

    if (initialMode === 'register') {
      this.actions.trackRegistrationTabOpened();
    }

    this.form.controls.authMode.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((mode) => {
        this.handleAuthModeChange(mode);
      });

    this.form.controls.email.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((email) => {
        this.store.patchAccount({ login: email });
        this.clearEmailErrors();
      });
  }

  isEmailEmpty(): boolean {
    return !this.form.controls.email.value.trim();
  }

  isSubmitDisabled(): boolean {
    const needsOfferAgreement =
      this.authMode === 'register' && !this.form.controls.agreeOffer.value;

    return this.isEmailEmpty() || needsOfferAgreement;
  }

  onOfferLinkClick(event: Event): void {
    event.stopPropagation();
    if (this.authMode === 'register') {
      this.authAnalytics.captureRegistrationOfferLinkOpened();
    }
  }

  async submit(): Promise<void> {
    if (this.isSubmitDisabled()) {
      return;
    }

    this.keyboardHold.hold();

    const emailControl = this.form.controls.email;
    const email = emailControl.value.trim();
    this.store.patchAccount({ login: email });
    this.authErrorMessage.set(null);

    if (!this.toolsService.emailValid(email)) {
      this.keyboardHold.release();
      this.setInvalidEmailError();
      return;
    }

    this.clearInvalidEmailError();
    const result = await this.actions.sendCode();
    if (!result.success) {
      this.keyboardHold.release();
      if (result.message) {
        this.authErrorMessage.set(result.message);
      }
    }
  }

  private handleAuthModeChange(mode: AuthMode): void {
    this.clearEmailErrors();

    const email = this.form.controls.email.value.trim();
    if (email) {
      this.store.patchAccount({ login: email });
    }

    if (mode === 'login' && this.flow.flowId() !== AuthFlowId.Login) {
      this.flow.switchBetweenLoginAndRegistration(AuthFlowId.Login);
      return;
    }

    if (mode === 'register' && this.flow.flowId() !== AuthFlowId.Registration) {
      this.flow.switchBetweenLoginAndRegistration(AuthFlowId.Registration);
      this.actions.trackRegistrationTabOpened();
    }
  }

  private clearEmailErrors(): void {
    this.authErrorMessage.set(null);
    this.clearInvalidEmailError();
  }

  private setInvalidEmailError(): void {
    const control = this.form.controls.email;
    control.markAsDirty();
    control.markAsTouched();
    control.setErrors({ ...(control.errors ?? {}), invalidEmail: true });
  }

  private clearInvalidEmailError(): void {
    const control = this.form.controls.email;
    if (!control.hasError('invalidEmail')) {
      return;
    }

    const errors = { ...(control.errors ?? {}) };
    delete errors['invalidEmail'];
    control.setErrors(Object.keys(errors).length ? errors : null);
  }
}
